import { defineStore } from "pinia";
import { ref } from "vue";
import router from "@/router";
import { useSessionStorage } from "@vueuse/core";
import http from "@/api/http";
import type { User } from "@/types/auth";

export const useAuthStore = defineStore("auth_store", () => {
  // User is never persisted to storage: it must always come fresh from
  // GET /auth/me so the app reflects the server's view of who is logged
  // in (roles/permissions/profile changes) on every reload.
  const user = ref<User | null>(null);

  const access_token = useSessionStorage<string | null>(
    "RAC_ACCESS_TOKEN",
    null,
  );

  const fetchUser = async () => {
    try {
      const response = await http().get("auth/me");
      user.value = response?.data?.data ?? null;
    } catch (err) {
      user.value = null;
      throw err;
    }
    return user.value;
  };

  const saveSession = (newUser: User, token: string) => {
    user.value = newUser;
    access_token.value = token;
  };

  const removeSession = () => {
    user.value = null;
    access_token.value = null;
    router.push({ name: "auth.login" });
  };

  const isAuthenticated = () =>
    user.value != null && access_token.value != null;

  return {
    user,
    access_token,
    fetchUser,
    saveSession,
    removeSession,
    isAuthenticated,
  };
});
